import { AlertCircle } from 'lucide-react'
import { EmptyState } from '@/components/ui/empty-state'
import { getFirstName } from '@/features/auth/api/auth'
import { useAuthStore } from '@/features/auth/authStore'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { useBollette, useNews, usePromozioni } from '@/lib/graphql/hooks'
import { AntitruffaBanner } from './components/AntitruffaBanner'
import { AutoletturaCard } from './components/AutoletturaCard'
import { HeroBillsChart } from './components/HeroBillsChart'
import { HeroBillsChartSkeleton } from './components/HeroBillsChart.skeleton'
import { NewsCarousel } from './components/NewsCarousel'
import { NewsCarouselSkeleton } from './components/NewsCarousel.skeleton'
import { PromozioniCarousel } from './components/PromozioniCarousel'
import { UltimaBollettaCard } from './components/UltimaBollettaCard'
import { UltimaBollettaCardSkeleton } from './components/UltimaBollettaCard.skeleton'
import { adaptAutolettura, adaptInvoice } from './adapters'

export function HomePage() {
  const user = useAuthStore((s) => s.user)
  const greeting = getGreeting()
  const firstName = user ? getFirstName(user) : null

  const {
    cliente,
    activeContract,
    isLoading: isLoadingContract,
    error: errorContract,
  } = useActiveContract()

  const codiceCliente = cliente?.codice_cliente
  const codiceContratto = activeContract?.codice_contratto

  const bolletteQ = useBollette(codiceCliente, codiceContratto)
  const newsQ = useNews()
  const promozioniQ = usePromozioni()

  // Errore bloccante: cliente/contratto non recuperabili → messaggio
  if (errorContract) {
    return (
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">
          {greeting}
          {firstName && (
            <>
              , <span className="text-primary">{firstName}</span>
            </>
          )}
        </h1>
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Non siamo riusciti a caricare i tuoi dati"
          description={errorContract.message}
        />
      </div>
    )
  }

  const showSkeleton = isLoadingContract || !activeContract
  const autolettura = activeContract ? adaptAutolettura(activeContract) : null
  const ultimaBolletta = adaptInvoice(bolletteQ.data?.[0], activeContract)

  // Cerca un alert sul contratto attivo di tipo "bolletta" (es. emessa / da pagare):
  // se presente, lo passiamo a UltimaBollettaCard per evidenziare la card
  // (chip "Nuova" o bordo warning + shadow).
  const bollettaAlert = activeContract?.alerts?.find((a) =>
    /bollett/i.test(a.tipo),
  )

  const addressLabel =
    activeContract?.pod.etichetta ?? activeContract?.pod.indirizzo ?? null

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">
          {greeting}
          {firstName && (
            <>
              , <span className="text-primary">{firstName}</span>
            </>
          )}
        </h1>
      </header>

      {autolettura && autolettura.mode !== 'closed' && (
        <AutoletturaCard window={autolettura} />
      )}

      <section className="grid items-stretch gap-4 lg:grid-cols-5">
        <div className="flex min-w-0 flex-col lg:col-span-3 [&>*]:flex-1">
          {showSkeleton || bolletteQ.isLoading ? (
            <HeroBillsChartSkeleton />
          ) : (
            <HeroBillsChart invoices={bolletteQ.data} addressLabel={addressLabel} />
          )}
        </div>
        <div className="flex min-w-0 flex-col lg:col-span-2 [&>*]:flex-1">
          {showSkeleton || bolletteQ.isLoading ? (
            <UltimaBollettaCardSkeleton />
          ) : (
            <UltimaBollettaCard
              invoice={ultimaBolletta}
              alertStato={bollettaAlert?.stato}
            />
          )}
        </div>
      </section>

      {newsQ.isLoading ? (
        <NewsCarouselSkeleton />
      ) : (
        <NewsCarousel items={newsQ.data ?? []} />
      )}

      {!promozioniQ.isLoading && (
        <PromozioniCarousel
          items={promozioniQ.data ?? []}
          customerName={cliente?.nome_completo}
          codiceCliente={cliente?.codice_cliente}
        />
      )}

      <AntitruffaBanner />
    </div>
  )
}

function getGreeting(): string {
  const h = new Date().getHours()
  if (h < 6) return 'Buonanotte'
  if (h < 12) return 'Buongiorno'
  if (h < 18) return 'Buon pomeriggio'
  return 'Buonasera'
}
